import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import FormComp from "../../../Componentes/FormComp";
import Formulario from "../../../Componentes/Utils/Formulario";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";
import { BsPencil } from "react-icons/bs";
import { obraLogoPublicUrl } from "../obraLogoPublicUrl";

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    padding: 0 4px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const ToolbarBtn = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background-color: #007bff;
    color: #fff;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.2s;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    &:hover {
        background-color: #0056b3;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const TableWrap = styled.div`
    overflow-x: auto;
    overflow-y: auto;
    max-height: min(520px, calc(90vh - 200px));
    border: 1px solid #e5e7eb;
    border-radius: 10px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    min-width: 720px;
`;

const Th = styled.th`
    text-align: left;
    padding: 10px 8px;
    background: #f3f4f6;
    color: #374151;
    font-weight: 700;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 1;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 11px;
`;

const Td = styled.td`
    padding: 8px;
    vertical-align: middle;
    border-bottom: 1px solid #f3f4f6;
`;

const CellText = styled.span`
    display: block;
    font-size: 12px;
    color: #1f2937;
    line-height: 1.35;
    word-break: break-word;
`;

const EditIconBtn = styled.button`
    padding: 8px;
    border: 1px solid #007bff;
    border-radius: 8px;
    background-color: #fff;
    color: #007bff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    transition: background-color 0.2s;
    &:hover {
        background-color: #e7f1ff;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const EditObraPencilIcon = styled(BsPencil)`
    width: 16px;
    height: 16px;
`;

const LogoPickBtn = styled.button`
    position: relative;
    width: 52px;
    height: 52px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    appearance: none;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover .logo-hover-hint {
        opacity: 1;
    }
    &:focus-visible {
        outline: 2px solid #007bff;
        outline-offset: 2px;
    }
`;

const LogoHoverHint = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
`;

const HoverPencilIcon = styled(BsPencil)`
    width: 18px;
    height: 18px;
    color: #fff;
    flex-shrink: 0;
`;

const LogoImg = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: auto;
    object-fit: contain;
    display: block;
`;

const LogoPlaceholder = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    box-sizing: border-box;
    font-size: 8px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.15;
`;

/** Logo na tabela: hover mostra ícone de lápis; clique abre seletor de arquivo. */
function TabelaLogoCell({ row, uploading, fileInputRef, onOpenPicker, onFileChange }) {
    const serverUrl = useMemo(() => obraLogoPublicUrl(row.logo_name), [row.logo_name]);
    const [broken, setBroken] = useState(false);

    useEffect(() => {
        setBroken(false);
    }, [serverUrl]);

    const showImg = Boolean(serverUrl && !broken);

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
                onChange={onFileChange}
                disabled={uploading}
            />
            <LogoPickBtn
                type="button"
                disabled={uploading}
                onClick={onOpenPicker}
                title="Editar logo"
            >
                {showImg ? (
                    <LogoImg src={serverUrl} alt="" onError={() => setBroken(true)} />
                ) : (
                    <LogoPlaceholder>Sem logo</LogoPlaceholder>
                )}
                <LogoHoverHint className="logo-hover-hint" aria-hidden>
                    <HoverPencilIcon />
                </LogoHoverHint>
            </LogoPickBtn>
        </>
    );
}

function ModalEditarObra({ obra, onClose, api, onRefresh, ambiente, setAmbiente }) {
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState({
        identificador: obra.identificador ?? "",
        nomeEmpresa: obra.nome_empresa ?? "",
        tipoIndustria: obra.tipo_industria ?? "",
        cidade: obra.cidade ?? "",
        estado: obra.estado ?? "",
    });

    useEffect(() => {
        setFormValues({
            identificador: obra.identificador ?? "",
            nomeEmpresa: obra.nome_empresa ?? "",
            tipoIndustria: obra.tipo_industria ?? "",
            cidade: obra.cidade ?? "",
            estado: obra.estado ?? "",
        });
    }, [obra]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value.toUpperCase(),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("id", String(obra.id));
            fd.append("identificador", formValues.identificador.trim());
            fd.append("nomeEmpresa", formValues.nomeEmpresa.trim());
            fd.append("tipoIndustria", formValues.tipoIndustria.trim());
            fd.append("cidade", formValues.cidade.trim());
            fd.append("estado", formValues.estado.trim());

            const { data } = await api.post("/Painel/API/ControleMedicao/UpdateObra/", fd);

            if (typeof onRefresh === "function") {
                await onRefresh();
            }

            const atual = data?.obra;
            if (atual && setAmbiente && ambiente?.id === atual.id) {
                setAmbiente({
                    id: atual.id,
                    descricao: atual.descricao,
                    logo_name: atual.logo_name,
                    identificador: atual.identificador,
                    nome_empresa: atual.nome_empresa,
                    tipo_industria: atual.tipo_industria,
                    cidade: atual.cidade,
                    estado: atual.estado,
                });
            }

            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Overlay state={true}>
            <Modal header close={onClose} size="md">
                <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", color: "#111827" }}>Editar obra</h3>
                <Formulario onSubmit={handleSubmit}>
                    <FormComp
                        title="IDENTIFICADOR"
                        type="text"
                        name="identificador"
                        valor={formValues.identificador}
                        changeFunc={handleChange}
                        maxLength={3}
                    />
                    <FormComp
                        title="NOME EMPRESA"
                        type="text"
                        name="nomeEmpresa"
                        valor={formValues.nomeEmpresa}
                        changeFunc={handleChange}
                    />
                    <FormComp
                        title="TIPO INDUSTRIA"
                        type="text"
                        name="tipoIndustria"
                        valor={formValues.tipoIndustria}
                        changeFunc={handleChange}
                    />
                    <FormComp
                        title="CIDADE"
                        type="text"
                        name="cidade"
                        valor={formValues.cidade}
                        changeFunc={handleChange}
                    />
                    <FormComp
                        title="ESTADO"
                        type="text"
                        maxLength={2}
                        name="estado"
                        valor={formValues.estado}
                        changeFunc={handleChange}
                    />
                    <FormComp type="submit" valor={loading ? "SALVANDO..." : "SALVAR"} disabled={loading} />
                </Formulario>
            </Modal>
        </Overlay>
    );
}

export default function ModalGerenciarObras({ listaAmbientes, onRefresh, ambiente, setAmbiente }) {
    const { closeModal, openModal } = useModal();
    const api = useApi();
    const [obraParaEditar, setObraParaEditar] = useState(null);
    const [uploadingLogoId, setUploadingLogoId] = useState(null);
    const logoFileInputsRef = useRef({});

    const handleClose = () => closeModal();

    const openNovaObra = () => {
        openModal("novoAmbienteMedicao");
    };

    const rows = useMemo(
        () =>
            listaAmbientes.map((r) => ({
                id: r.id,
                identificador: (r.identificador ?? "").toString(),
                nome_empresa: (r.nome_empresa ?? "").toString(),
                tipo_industria: (r.tipo_industria ?? "").toString(),
                cidade: (r.cidade ?? "").toString(),
                estado: (r.estado ?? "").toString(),
                descricao: r.descricao,
                logo_name: r.logo_name,
            })),
        [listaAmbientes]
    );

    const aplicarObraAtualizadaNoContexto = (atual) => {
        if (atual && setAmbiente && ambiente?.id === atual.id) {
            setAmbiente({
                id: atual.id,
                descricao: atual.descricao,
                logo_name: atual.logo_name,
                identificador: atual.identificador,
                nome_empresa: atual.nome_empresa,
                tipo_industria: atual.tipo_industria,
                cidade: atual.cidade,
                estado: atual.estado,
            });
        }
    };

    const handleLogoNaTabela = async (row, e) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = "";
        if (!file) return;

        setUploadingLogoId(row.id);
        try {
            const fd = new FormData();
            fd.append("id", String(row.id));
            fd.append("identificador", row.identificador.trim());
            fd.append("nomeEmpresa", row.nome_empresa.trim());
            fd.append("tipoIndustria", row.tipo_industria.trim());
            fd.append("cidade", row.cidade.trim());
            fd.append("estado", row.estado.trim());
            fd.append("logo", file);

            const { data } = await api.post("/Painel/API/ControleMedicao/UpdateObra/", fd);

            if (typeof onRefresh === "function") {
                await onRefresh();
            }

            aplicarObraAtualizadaNoContexto(data?.obra);
        } catch (err) {
            console.error(err);
        } finally {
            setUploadingLogoId(null);
        }
    };

    return (
        <>
            <Overlay state={true}>
                <Modal header close={handleClose} size="xl" noPadding>
                    <div style={{ padding: "20px" }}>
                        <Toolbar>
                            <Title>Gerenciar obras</Title>
                            <ToolbarBtn type="button" onClick={openNovaObra}>
                                Nova obra
                            </ToolbarBtn>
                        </Toolbar>

                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th style={{ width: 72 }}>Logo</Th>
                                        <Th style={{ width: 88 }}>ID</Th>
                                        <Th>Empresa</Th>
                                        <Th>Tipo indústria</Th>
                                        <Th>Cidade</Th>
                                        <Th style={{ width: 64 }}>UF</Th>
                                        <Th style={{ width: 56 }} scope="col" aria-label="Editar obra">
                                            {"\u00a0"}
                                        </Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <Td colSpan={7} style={{ textAlign: "center", padding: "24px", color: "#6b7280" }}>
                                                Nenhuma obra cadastrada. Use &quot;Nova obra&quot; para incluir.
                                            </Td>
                                        </tr>
                                    ) : (
                                        rows.map((row) => (
                                            <tr key={row.id}>
                                                <Td>
                                                    <TabelaLogoCell
                                                        row={row}
                                                        uploading={uploadingLogoId === row.id}
                                                        fileInputRef={(el) => {
                                                            logoFileInputsRef.current[row.id] = el;
                                                        }}
                                                        onOpenPicker={() => logoFileInputsRef.current[row.id]?.click()}
                                                        onFileChange={(ev) => handleLogoNaTabela(row, ev)}
                                                    />
                                                </Td>
                                                <Td>
                                                    <CellText>{row.identificador}</CellText>
                                                </Td>
                                                <Td>
                                                    <CellText>{row.nome_empresa}</CellText>
                                                </Td>
                                                <Td>
                                                    <CellText>{row.tipo_industria || "—"}</CellText>
                                                </Td>
                                                <Td>
                                                    <CellText>{row.cidade}</CellText>
                                                </Td>
                                                <Td>
                                                    <CellText>{row.estado}</CellText>
                                                </Td>
                                                <Td>
                                                    <EditIconBtn
                                                        type="button"
                                                        title="Editar obra"
                                                        aria-label="Editar obra"
                                                        onClick={() => setObraParaEditar(row)}
                                                    >
                                                        <EditObraPencilIcon aria-hidden />
                                                    </EditIconBtn>
                                                </Td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </div>
                </Modal>
            </Overlay>

            {obraParaEditar !== null && (
                <ModalEditarObra
                    obra={obraParaEditar}
                    onClose={() => setObraParaEditar(null)}
                    api={api}
                    onRefresh={onRefresh}
                    ambiente={ambiente}
                    setAmbiente={setAmbiente}
                />
            )}
        </>
    );
}
